/**
 * SoundController - Modular sound effects system for Lion's Den
 *
 * Provides centralized sound management with:
 * - Declarative sound mapping via sound-config.json
 * - Volume and mute controls
 * - Sound asset preloading and caching (fetch + decodeAudioData)
 * - Simple play(eventName) interface
 * - Drop-in compatibility with existing plugin architecture
 *
 * Playback runs on Web Audio through the shared self-healing AudioEngine
 * (see AudioEngine.js) so iOS interruptions, zombie contexts, and audio
 * session failures recover automatically. There is deliberately no
 * iOS-specific unlock code here anymore — the engine's permanent
 * capture-phase gesture listeners own that.
 */

import { getAudioCtx, audioLog } from './AudioEngine'

class SoundController {
  constructor() {
    this.sounds = new Map()   // eventName → AudioBuffer (context-independent)
    this.rawSounds = new Map() // eventName → ArrayBuffer awaiting decode
    this.activeSources = new Map() // playing BufferSource → GainNode
    this.config = null
    this.volume = 0.7
    this.muted = false
    this.initialized = false
    this.loadingPromise = null

    // Bind methods to preserve context
    this.play = this.play.bind(this)
    this.setVolume = this.setVolume.bind(this)
    this.setMuted = this.setMuted.bind(this)
    this.unlockAudio = this.unlockAudio.bind(this)
  }

  /**
   * Initialize the SoundController by loading configuration and preloading sounds
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    this.loadingPromise = this._initialize()
    return this.loadingPromise
  }

  async _initialize() {
    try {
      // Load sound configuration
      const response = await fetch('/sound/sound-config.json')
      if (!response.ok) {
        throw new Error('Failed to load sound configuration')
      }

      this.config = await response.json()

      await this._preloadSounds()

      this.initialized = true
    } catch (error) {
      console.warn('SoundController: Failed to initialize', error)
      // Continue without sounds rather than breaking the app
      this.initialized = false
    }
  }

  /**
   * Detect if running on iOS device
   * @private
   */
  _isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }

  /**
   * Fetch all sound assets as ArrayBuffers and decode what we can.
   * Fetching isn't gesture-gated on any platform, so this runs everywhere
   * (including iOS). Decoding needs a context; failures keep the raw bytes
   * around for a retry at play time.
   * @private
   */
  async _preloadSounds() {
    if (!this.config || !this.config.events) {
      return
    }

    const loadPromises = Object.entries(this.config.events).map(async ([eventName, soundPath]) => {
      try {
        const response = await fetch(soundPath)
        if (!response.ok) {
          console.warn(`SoundController: Failed to fetch sound for ${eventName}: ${response.status}`)
          return
        }
        this.rawSounds.set(eventName, await response.arrayBuffer())
        await this._decode(eventName)
      } catch (error) {
        console.warn(`SoundController: Error preloading sound for ${eventName}:`, error)
      }
    })

    await Promise.all(loadPromises)
  }

  /**
   * Decode an event's raw bytes into an AudioBuffer and cache it.
   * AudioBuffers are context-independent, so they survive engine rebuilds.
   * Decodes from a copy so the raw bytes stay usable for a retry.
   * @private
   * @returns {Promise<AudioBuffer|null>}
   */
  async _decode(eventName) {
    const cached = this.sounds.get(eventName)
    if (cached) return cached

    const raw = this.rawSounds.get(eventName)
    if (!raw) return null

    const ctx = getAudioCtx()
    if (!ctx) return null

    try {
      const buffer = await ctx.decodeAudioData(raw.slice(0))
      this.sounds.set(eventName, buffer)
      this.rawSounds.delete(eventName)
      return buffer
    } catch (error) {
      console.warn(`SoundController: Failed to decode sound for ${eventName}:`, error)
      return null
    }
  }

  /**
   * Play a sound effect by event name.
   * Returns a Promise that resolves when the sound (and any `then`-chained sound) ends.
   * Resolves immediately on mute/error/unsupported so callers never hang.
   *
   * @param {string} eventName - The event name to play (e.g., 'player_joined', 'game_started')
   * @param {Object} options - Optional playback options
   * @param {number} options.volume - Override volume for this play (0-1)
   * @param {boolean} options.loop - Whether to loop the sound
   * @param {string} options.then - Event name to play automatically when this sound ends
   * @returns {Promise<void>} Resolves when the full sound chain finishes
   */
  async play(eventName, options = {}) {
    if (!this.initialized || this.muted) {
      return
    }

    const ctx = getAudioCtx()
    // Never schedule onto a non-running clock (suspended / iOS 'interrupted'
    // / zombie): queued sources would all fire in a burst when the context
    // eventually resumes. Skipping is the correct failure mode — the engine's
    // gesture listeners repair the context for the next play.
    if (!ctx || ctx.state !== 'running') {
      audioLog(`▶ ${eventName} SKIPPED: state=${ctx ? ctx.state : 'no ctx'}`)
      return
    }

    const buffer = this.sounds.get(eventName) || await this._decode(eventName)
    if (!buffer) {
      console.warn(`SoundController: No sound found for event: ${eventName}`)
      return
    }

    // Promise that resolves when the full sound chain finishes
    let endedResolve
    const endedPromise = new Promise((resolve) => { endedResolve = resolve })

    try {
      const source = ctx.createBufferSource()
      source.buffer = buffer
      if (options.loop) {
        source.loop = true
      }

      const gainNode = ctx.createGain()
      const playVolume = options.volume !== undefined ? options.volume : this.volume
      gainNode.gain.value = Math.max(0, Math.min(1, playVolume))

      source.connect(gainNode)
      gainNode.connect(ctx.destination)

      this.activeSources.set(source, gainNode)

      source.onended = () => {
        this.activeSources.delete(source)
        // Chain a follow-up sound; resolve endedPromise when the chain finishes
        // (but never chain out of a stopAll() — that should end the chain too)
        if (options.then && !source._stoppedByController) {
          if (options.onChain) options.onChain()
          this.play(options.then).then(endedResolve).catch(endedResolve)
        } else {
          endedResolve()
        }
      }

      audioLog(`▶ ${eventName}: state=${ctx.state} clock=${ctx.currentTime.toFixed(3)}`)
      source.start(0)
    } catch (error) {
      console.warn(`SoundController: Error playing sound for ${eventName}:`, error)
      endedResolve()
      return
    }

    return endedPromise
  }

  /**
   * Set the master volume for all sounds
   * @param {number} volume - Volume level (0-1)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume))

    // Update volume for currently playing sounds (matters for loops)
    this.activeSources.forEach((gainNode) => {
      try {
        gainNode.gain.value = this.volume
      } catch { /* node's context was closed by an engine rebuild */ }
    })
  }

  /**
   * Get the current master volume
   * @returns {number} Current volume (0-1)
   */
  getVolume() {
    return this.volume
  }

  /**
   * Set muted state
   * @param {boolean} muted - Whether sounds should be muted
   */
  setMuted(muted) {
    this.muted = Boolean(muted)
  }

  /**
   * Get current muted state
   * @returns {boolean} Whether sounds are muted
   */
  isMuted() {
    return this.muted
  }

  /**
   * Toggle muted state
   * @returns {boolean} New muted state
   */
  toggleMuted() {
    this.setMuted(!this.muted)
    return this.muted
  }

  /**
   * Check if SoundController is ready to play sounds
   * @returns {boolean} Whether the controller is initialized
   */
  isReady() {
    return this.initialized
  }

  /**
   * Public method to check if running on iOS (for UI components)
   * @returns {boolean} Whether running on iOS
   */
  isIOS() {
    return this._isIOS()
  }

  /**
   * Check if audio can be played right now
   * @returns {boolean} Whether audio can be played
   */
  canPlayAudio() {
    if (!this.initialized) return false
    const ctx = getAudioCtx()
    return !!ctx && ctx.state === 'running'
  }

  /**
   * Legacy manual unlock hook. The engine's permanent gesture listeners
   * already ran (capture phase) by the time any click handler calls this,
   * so it only nudges the context and reports the result.
   */
  async unlockAudio() {
    getAudioCtx()
    return Promise.resolve()
  }

  /**
   * Get list of available sound events
   * @returns {string[]} Array of event names
   */
  getAvailableEvents() {
    if (!this.config || !this.config.events) {
      return []
    }
    return Object.keys(this.config.events)
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll() {
    this.activeSources.forEach((gainNode, source) => {
      try {
        source._stoppedByController = true
        source.stop()
      } catch { /* already stopped or context closed */ }
    })
    this.activeSources.clear()
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.stopAll()
    this.sounds.clear()
    this.rawSounds.clear()
    this.config = null
    this.initialized = false
    this.loadingPromise = null
  }
}

// Create and export a singleton instance
const soundController = new SoundController()

export default soundController
